import React from 'react';
import { useTranslation } from 'react-i18next';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { OrderingSelectors, OrderingOperations, OrderingConstants } from 'polygon-ordering';
import Modal from '../components/Modal';
import StandardButton from '../components/StandardButton';
import Text from '../components/Text'; // TODO: rework this to use Text component
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { Col, Row } from 'reactstrap';
import TouchableOpacity from '../components/TouchableOpacity';
import combineStyles from '../utils/combineStyles';
import { setDesiredDeliveryTime } from '../slices/desiredDeliveryTime';
import { DELIVERY_SALE_TYPES } from '../libs/polygon-ordering/src/constants';
export const CONFIRM_CHANGES_MODAL_ID = 'CONFIRM_CHANGES_MODAL_ID';

const { SALE_TYPE } = OrderingConstants;

const {
  getPickupTime,
  getBufferPickupTime,
  getSaleType,
  getDeliveryTimePrevious,
  getBufferDeliveryTime,
} = OrderingSelectors;

const { removeStagedPurchase, setPickupTime } = OrderingOperations;

const ConfirmChangesModal = ({
  title,
  message,
  previousChangesTitle,
  currentChangesTitle,
  confirmChangesTitle,
  confirmChangesDescription,
  titleStyle,
  desktopMaxWidth = 600,
  desktopMinWidth = 600,
}: {
  desktopMaxWidth: number;
  desktopMinWidth: number;
  title?: string;
  message?: string;
  previousChangesTitle?: string;
  currentChangesTitle?: string;
  confirmChangesTitle?: string;
  confirmChangesDescription?: string;
  titleStyle?: React.CSSProperties;
  //   closeButtonStyle?: ReactCSSProperties;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const modalParams = useAppSelector(state => state.modalParams);
  const pickupTime = useAppSelector(getPickupTime);
  const bufferPickupTime = useAppSelector(getBufferPickupTime);
  const deliveryTimePrevious = useAppSelector(getDeliveryTimePrevious);
  const saleType = useAppSelector(getSaleType);

  const desiredDeliveryTime = useAppSelector(state => state.desiredDeliveryTime);
  const bufferDeliveryTime = useAppSelector(getBufferDeliveryTime);

  const deliveryTime = desiredDeliveryTime || bufferDeliveryTime;

  const isDelivery = DELIVERY_SALE_TYPES.includes(saleType);

  const formatDate = (dateString: string) => {
    const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
      weekday: 'short', // e.g., "Tuesday"
      day: 'numeric', // "3"
      month: 'numeric', // "12"
      hour: 'numeric', // e.g., "9 PM"
      minute: '2-digit', // e.g., "35"
      timeZoneName: 'short', // e.g., "AEDT"
    };
    return new Date(dateString).toLocaleString('en-AU', DATE_FORMAT_OPTIONS);
  };

  const formatPreviousTime = () => {
    if (isDelivery) {
      if (deliveryTimePrevious === 'ASAP') {
        return 'ASAP';
      }
      return formatDate(deliveryTimePrevious! as string);
    }

    // Handle pickup time
    if (!pickupTime || pickupTime === 'ASAP' || pickupTime === null) {
      return 'ASAP';
    }

    return formatDate(pickupTime as string);
  };

  const formatCurrentTime = () => {
    if (isDelivery) {
      if (deliveryTime === 'ASAP') {
        return 'ASAP';
      }
      return formatDate(deliveryTime! as string);
    }

    // Handle pickup time
    if (!pickupTime || pickupTime === 'ASAP' || pickupTime === null) {
      return 'ASAP';
    }

    return formatDate(bufferPickupTime! as string);
  };

  // if it has coupons then maybe we do a validate cart inside here
  return (
    <Modal
      title={title ? title : modalParams?.title}
      desktopMaxWidth={desktopMaxWidth}
      desktopMinWidth={desktopMinWidth}
      desktopContainerStyle={styles.modalDesktopContainer}
      // hideFloatingHeader
      titleStyle={titleStyle}
      handleClose={() => {
        if (isDelivery) {
          dispatch(setDesiredDeliveryTime(bufferPickupTime! as string));
        }
        dispatch(setCurrentModal(null));
      }}
      wrapperStyle={styles.wrapperStyle}
      mobileContainerStyle={styles.mobileContainerStyle}
    >
      {(modalParams?.message || message) && (
        <Text
          style={{ textAlign: 'center' }}
          block
          value={modalParams?.message ?? message}
          themeKey="confirmChangesModalMessage"
        />
      )}

      <Row style={{ padding: '0 30px' }}>
        <Col sm={{ size: 6 }}>
          <div style={window.matchMedia('(max-width: 768px)').matches ? { paddingTop: 30 } : {}}>
            <Text
              block
              value={
                previousChangesTitle?.toUpperCase() ??
                modalParams?.previousChangesTitle?.toUpperCase()
              }
              themeKey="confirmChangesPreviousChangesTitle"
            />
            <Text
              block
              style={window.matchMedia('(max-width: 768px)').matches ? { paddingLeft: 30 } : {}}
              value={formatPreviousTime()}
              themeKey="confirmChangesPreviousChangesTime"
            />
          </div>
        </Col>
        <Col sm={{ size: 6 }}>
          <div style={window.matchMedia('(max-width: 768px)').matches ? { paddingTop: 30 } : {}}>
            <Text
              block
              value={
                currentChangesTitle?.toUpperCase() ??
                modalParams?.currentChangesTitle?.toUpperCase()
              }
              themeKey="confirmChangesCurrentChangesTitle"
            />
            <Text
              block
              style={window.matchMedia('(max-width: 768px)').matches ? { paddingLeft: 30 } : {}}
              value={formatCurrentTime()}
              themeKey="confirmChangesCurrentChangesTime"
            />
          </div>
        </Col>
      </Row>

      <Row style={{ padding: '0 30px' }}>
        <Text
          block
          value={
            confirmChangesTitle?.toUpperCase() ?? modalParams?.confirmChangesTitle?.toUpperCase()
          }
          themeKey="confirmChangesSubsectionTitle"
        />
        <Text
          block
          value={
            confirmChangesDescription ??
            modalParams?.confirmChangesDescription ??
            t('confirmChanges.confirmChangesSubsectionDescription', { purchase: 'item' })
          }
          themeKey="confirmChangesPreviousChangesTime"
        />
      </Row>

      <Row style={{ padding: '0 30px' }}>
        {modalParams?.changesBreakdown &&
          Array.isArray(modalParams?.changesBreakdown) &&
          modalParams?.changesBreakdown.map(item => (
            <Text
              style={window.matchMedia('(max-width: 768px)').matches ? { paddingLeft: 50 } : {}}
              block
              value={item.name}
              themeKey="changesBreakDown"
            />
          ))}
      </Row>

      <Row
        style={{
          padding: '50px 30px 0 30px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <StandardButton
          label={modalParams?.buttonText ?? t('button.confirm')}
          onClick={
            modalParams?.action ??
            (() => {
              if (modalParams?.changesBreakdown && Array.isArray(modalParams?.changesBreakdown)) {
                modalParams?.changesBreakdown.map(item => dispatch(removeStagedPurchase(item.id)));
              }

              if (saleType === SALE_TYPE.PICKUP) {
                dispatch(setPickupTime(bufferPickupTime! as string));
              }

              dispatch(setCurrentModal(null));
            })
          }
          themeKey="messageModalButton"
          style={{ marginTop: 'auto' }}
        />
      </Row>

      <TouchableOpacity
        style={{ padding: '0 30px', textAlign: 'center' }}
        onClick={() => {
          dispatch(setCurrentModal(null));
          if (isDelivery) {
            dispatch(setDesiredDeliveryTime(bufferPickupTime! as string));
          }
        }}
        ariaLabel={'Back to previous page'}
      >
        <Text
          themeKey="confirmChangesRevertLink"
          value={t('confirmChanges.revertLink')}
          style={combineStyles(
            styles.label,
            // p('labelThemeKey', TEXT_PROPERTIES),
          )}
        />
      </TouchableOpacity>
    </Modal>
  );
};

const styles: Styles = {
  modalDesktopContainer: {
    gap: 25,
    paddingBottom: 40,
  },
  wrapperStyle: {
    height: '80%',
  },
  mobileContainerStyle: {
    paddingTop: 100,
    paddingLeft: 0,
    paddingRight: 0,
    height: '100%',
  },
};

export default ConfirmChangesModal;
